import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  languageName,
  type LanguageCode,
  type LanguageConfiguration,
} from "@/config/language-rules";

// Radix Select does not allow an empty item value, so the default choice uses this stand-in.
const DEFAULT_VALUE = "__default__";

interface StartLanguageSelectProps {
  setting: LanguageConfiguration | null;
  /** A language code, or '' for the assistant default. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** The languages a call may start in: the setting's languages in order, plus English. */
function allowedLanguages(setting: LanguageConfiguration): LanguageCode[] {
  return setting.languages.includes("en")
    ? setting.languages
    : [...setting.languages, "en"];
}

export function StartLanguageSelect({
  setting,
  value,
  onChange,
  disabled,
}: StartLanguageSelectProps) {
  if (!setting) return null;
  if (setting.languages.length === 1 && setting.languages[0] === "en") return null;

  return (
    <div className="w-full">
      <p className="text-sm font-medium text-muted-foreground mb-2">
        Start language
      </p>
      <Select
        value={value || DEFAULT_VALUE}
        onValueChange={(next) => onChange(next === DEFAULT_VALUE ? "" : next)}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_VALUE}>
            Assistant default ({languageName(setting.primary)})
          </SelectItem>
          {allowedLanguages(setting).map((code) => (
            <SelectItem key={code} value={code}>
              {languageName(code)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-2 text-xs text-muted-foreground">
        The call starts in this language. The caller can still ask for another.
      </p>
    </div>
  );
}
